import { toast } from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import { updateProduct } from 'modules/products/api/product-api'
import ProductForm from 'modules/products/components/product-form'
import { emptyProductErrorsState } from 'modules/products/constants/product-errors'
import { ProductsLoco } from 'modules/products/constants/products-loco'
import { useMaxVariants } from 'modules/products/hooks/use-max-variants'
import { useProduct } from 'modules/products/hooks/use-products'
import { ProductErrorResponseContentInterface } from 'modules/products/types/error-response-content-interface'
import { ProductSaveInterface } from 'modules/products/types/product-interface'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Loader } from 'shared/components/loader'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

function ProductPage() {
  const { t } = useLocoTranslation()

  const [isFetching, setIsFetching] = useState(false)
  const [validationErrorsNotification, setValidationErrorsNotification] = useState(false)

  const router = useRouter()
  const { productId } = router.query
  const { data: product, isValidating } = useProduct(Number(productId))
  const { maxVariants } = useMaxVariants()

  const { revalidateModules } = useRevalidateModules()

  const handleUpdateProduct = async (productSaveData: ProductSaveInterface) => {
    if (!product) return

    setIsFetching(true)

    const { productData, setErrors, setVariantErrors } = productSaveData

    try {
      setErrors(emptyProductErrorsState)
      setValidationErrorsNotification(false)
      await updateProduct(Number(product.id), productData)
      setIsFetching(false)
      await revalidateModules([RequestModuleEnum.PhysicalProducts])
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const productErrors = e.errors as unknown as ProductErrorResponseContentInterface
        setErrors({
          name: productErrors.fields.name?.join(''),
          currency: productErrors.fields.currency?.join(''),
          price: productErrors.fields.price?.join(''),
          sku: productErrors.fields.sku?.join(''),
          tax: productErrors.fields.tax?.join(''),
          weight: productErrors.fields.weight?.join(''),
          common: productErrors.common,
          options: (productErrors.fields.options as Record<string, Record<string, string[]>>) || {},
          stock: productErrors.fields.stock?.join(''),
          shippingFeesDisabled: productErrors.fields.shippingFeesDisabled?.join(''),
          taxBehavior: productErrors.fields.taxBehavior?.join(''),
        })

        if (!!productErrors.fields?.productVariants) {
          setVariantErrors(productErrors.fields.productVariants)
        }

        if (!!productErrors.fields?.productVariantsLimit) {
          toast.error(productErrors.fields.productVariantsLimit?.join(''))
        }

        if (!!Object.values(productErrors.fields).length) setValidationErrorsNotification(true)
      } else if (e instanceof InternalError) {
        setErrors(prev => {
          return {
            ...prev,
            common: [t('core.error.internal_error_message')],
          }
        })
      }
    }
  }

  if (!product || isValidating || !maxVariants) {
    return (
      <div className="m-auto table">
        <Loader type="dark" />
      </div>
    )
  }

  return (
    <ProductForm
      // @ts-expect-error
      product={product}
      validationErrorsNotification={validationErrorsNotification}
      isFetching={isFetching}
      onSave={handleUpdateProduct}
      maxProductVariants={maxVariants.maxProductVariants}
      setValidationErrorsNotification={setValidationErrorsNotification}
    />
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ProductsLoco,
  'header.top_menu.physical_product',
)

export default ProductPage
